"use client"

import { useState, useRef } from "react"
import {
  X,
  Plus,
  Trash2,
  BookOpen,
  Target,
  Hash,
  Type,
  Loader2,
  Copy,
  FileText,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { postAws } from "../postAws"

const API_URL = "https://api-dev.aurait.ai/QuestionBank"

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null)

  const executeCommand = (command, value = null) => {
    editorRef.current.focus()
    document.execCommand(command, false, value)
    // Update parent component with new content after command execution
    setTimeout(() => {
      if (onChange) {
        onChange(editorRef.current.innerHTML)
      }
    }, 0)
  }

  const handleInput = (e) => {
    if (onChange) {
      onChange(e.target.innerHTML)
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData("text/plain")
    editorRef.current.focus()
    document.execCommand("insertText", false, text)
    if (onChange) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const handleKeyDown = (e) => {
    // Allow normal typing and navigation
    if (e.key === "Enter" && !e.shiftKey) {
      // Handle enter key properly
      document.execCommand("insertHTML", false, "<br><br>")
      e.preventDefault()
    }
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center space-x-1 p-2 border-b border-gray-200 bg-gray-50 flex-wrap">
        <button
          type="button"
          onClick={() => executeCommand("bold")}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand("italic")}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand("underline")}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Underline"
        >
          <Underline className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1"></div>

        <button
          type="button"
          onClick={() => executeCommand("justifyLeft")}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand("justifyCenter")}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => executeCommand("justifyRight")}
          className="p-2 hover:bg-gray-200 rounded transition-colors"
          title="Align Right"
        >
          <AlignRight className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 mx-1"></div>

        <select
          onChange={(e) => {
            if (e.target.value) {
              executeCommand("fontSize", e.target.value)
            }
          }}
          className="px-2 py-1 text-sm border border-gray-300 rounded"
          defaultValue=""
        >
          <option value="">Font Size</option>
          <option value="1">Small</option>
          <option value="3">Normal</option>
          <option value="5">Large</option>
          <option value="7">Extra Large</option>
        </select>

        <input
          type="color"
          onChange={(e) => executeCommand("foreColor", e.target.value)}
          className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
          title="Text Color"
          defaultValue="#000000"
        />
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        className="p-4 min-h-[150px] focus:outline-none"
        style={{
          minHeight: "150px",
          direction: "ltr",
          textAlign: "left",
        }}
        suppressContentEditableWarning={true}
      >
        {!value && <span className="text-gray-400 italic pointer-events-none">{placeholder}</span>}
      </div>
    </div>
  )
}

const AddQuestionAddExamType = ({ showModal, onClose }) => {
  const SUPPORTED_QUESTION_TYPES = [
    { value: "Single Correct Option", label: "Single Correct Option" },
    { value: "Multiple Correct", label: "Multiple Correct" },
    { value: "True/False", label: "True/False" },
    { value: "Fill in the Blanks", label: "Fill in the Blanks" },
    { value: "Integer Type", label: "Integer Type" },
    { value: "Comprehension", label: "Comprehension" },
    { value: "Assertion-Reasoning", label: "Assertion-Reasoning" },
    { value: "Matrix-Match", label: "Matrix-Match" },
    { value: "Paragraph", label: "Paragraph" },
  ]

  const defaultInstructions = {
    "Single Correct Option":
      "Multiple Choice Questions with 4 options where only 1 option is correct. Students must select the best answer from the given choices.",
    "Multiple Correct":
      "Multiple Choice Questions with 4 options where multiple options can be correct. Students must select all correct answers.",
    "Fill in the Blanks":
      "Questions where students need to fill in missing words or phrases in the given sentences or statements.",
    "True/False": "Statements that students must evaluate as either True or False based on their knowledge.",
    "Integer Type":
      "Questions requiring numerical answers. Students must enter the exact numerical value as the answer.",
    Comprehension:
      "Reading comprehension questions based on a given passage. Students must read and understand the passage to answer related questions.",
    "Assertion-Reasoning":
      "Questions with two statements - Assertion (A) and Reason (R). Students must evaluate the truth of both statements and their relationship.",
    "Matrix-Match":
      "Questions where students need to match items from two columns or matrices based on their relationships.",
    Paragraph: "Descriptive questions requiring detailed written answers in paragraph form.",
  }

  const [examForm, setExamForm] = useState({
    examName: "",
    examInstructions: "",
    subjects: [],
  })
  const [loading, setLoading] = useState(false)
  const [expandedInstructions, setExpandedInstructions] = useState({})

  const addSubject = () => {
    setExamForm((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        {
          name: "",
          totalQuestions: "",
          timeInMinutes: "",
          questionTypes: [
            {
              type: "",
              count: "",
              instructions: "",
              marksForCorrect: 1,
              marksForIncorrect: -1,
              marksForLeaving: 0,
            },
          ],
        },
      ],
    }))
  }

  const copyConfiguration = (sourceIndex) => {
    const sourceSubject = examForm.subjects[sourceIndex]

    const newSubject = {
      name: "", // Keep name empty for new subject
      totalQuestions: sourceSubject.totalQuestions,
      timeInMinutes: sourceSubject.timeInMinutes,
      questionTypes: sourceSubject.questionTypes.map((qType) => ({
        type: qType.type,
        count: qType.count,
        // Ensure instructions are copied - use existing instructions or default if empty
        instructions: qType.instructions || defaultInstructions[qType.type] || "",
        marksForCorrect: qType.marksForCorrect ,
        marksForIncorrect: qType.marksForIncorrect ,
        marksForLeaving: qType.marksForLeaving ,
      })),
    }

    setExamForm((prev) => ({
      ...prev,
      subjects: [...prev.subjects, newSubject],
    }))

    const newSubjectIndex = examForm.subjects.length
    const newExpandedStates = {}

    sourceSubject.questionTypes.forEach((qType, typeIndex) => {
      if (qType.type) {
        const newKey = `${newSubjectIndex}-${typeIndex}`
        // Always expand instructions for copied question types so user can see they were copied
        newExpandedStates[newKey] = true
      }
    })

    setExpandedInstructions((prev) => ({
      ...prev,
      ...newExpandedStates,
    }))
  }

  const removeSubject = (index) => {
    setExamForm((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index),
    }))
  }

  const updateSubject = (index, field, value) => {
    setExamForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) => (i === index ? { ...subject, [field]: value } : subject)),
    }))
  }

  const addQuestionType = (subjectIndex) => {
    setExamForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === subjectIndex
          ? {
              ...subject,
              questionTypes: [
                ...subject.questionTypes,
                {
                  type: "",
                  count: "",
                  instructions: "",
                  marksForCorrect: 1,
                  marksForIncorrect: -1,
                  marksForLeaving: 0,
                },
              ],
            }
          : subject,
      ),
    }))
  }

  const removeQuestionType = (subjectIndex, typeIndex) => {
    setExamForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === subjectIndex
          ? { ...subject, questionTypes: subject.questionTypes.filter((_, j) => j !== typeIndex) }
          : subject,
      ),
    }))
  }

  const updateQuestionType = (subjectIndex, typeIndex, field, value) => {
    setExamForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === subjectIndex
          ? {
              ...subject,
              questionTypes: subject.questionTypes.map((qType, j) => {
                if (j === typeIndex) {
                  if (field === "type" && value && defaultInstructions[value]) {
                    const key = `${subjectIndex}-${typeIndex}`
                    setExpandedInstructions((prev) => ({ ...prev, [key]: true }))
                    return { ...qType, [field]: value, instructions: qType.instructions || defaultInstructions[value] }
                  }
                  return { ...qType, [field]: value }
                }
                return qType
              }),
            }
          : subject,
      ),
    }))
  }

  const toggleInstructionExpansion = (subjectIndex, typeIndex) => {
    const key = `${subjectIndex}-${typeIndex}`
    setExpandedInstructions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const validateForm = () => {
    if (!examForm.examName.trim()) {
      alert("Please enter exam name")
      return false
    }

    if (examForm.subjects.length === 0) {
      alert("Please add at least one subject")
      return false
    }

    for (let i = 0; i < examForm.subjects.length; i++) {
      const subject = examForm.subjects[i]

      if (!subject.name.trim()) {
        alert(`Please enter name for subject ${i + 1}`)
        return false
      }

      if (!subject.totalQuestions || subject.totalQuestions <= 0) {
        alert(`Please enter valid total questions for ${subject.name}`)
        return false
      }

      if (!subject.timeInMinutes || subject.timeInMinutes <= 0) {
        alert(`Please enter valid time in minutes for ${subject.name}`)
        return false
      }

      let totalTypeQuestions = 0
      for (let j = 0; j < subject.questionTypes.length; j++) {
        const qType = subject.questionTypes[j]

        if (!qType.type.trim()) {
          alert(`Please enter question type ${j + 1} for ${subject.name}`)
          return false
        }

        if (!qType.count || qType.count <= 0) {
          alert(`Please enter valid count for ${qType.type} in ${subject.name}`)
          return false
        }

        totalTypeQuestions += Number.parseInt(qType.count)
      }

      if (totalTypeQuestions !== Number.parseInt(subject.totalQuestions)) {
        alert(
          `Question types total (${totalTypeQuestions}) must equal total questions (${subject.totalQuestions}) for ${subject.name}. Please adjust the counts.`,
        )
        return false
      }
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)

      const requestBody = {
        request: "create-exam-type",
        body: {
          examName: examForm.examName.trim(),
          examInstructions: examForm.examInstructions,
          subjects: examForm.subjects.map((subject) => ({
            name: subject.name.trim(),
            totalQuestions: Number.parseInt(subject.totalQuestions),
            timeInMinutes: Number.parseInt(subject.timeInMinutes),
            questionTypes: subject.questionTypes.map((qType) => ({
              type: qType.type.trim(),
              count: Number.parseInt(qType.count),
              instructions: qType.instructions.trim(),
              marksForCorrect: Number.parseFloat(qType.marksForCorrect) ,
              marksForIncorrect: Number.parseFloat(qType.marksForIncorrect),
              marksForLeaving: Number.parseFloat(qType.marksForLeaving) ,
            })),
          })),
          teacherId: localStorage.getItem("teacherId") || "",
          classroomId: localStorage.getItem("classRoomId") || "",
        },
      }

      const response = await postAws(API_URL, "POST", requestBody)

      if (response && response.statusCode === 200) {
        alert("Exam type created successfully!")
        setExamForm({ examName: "", examInstructions: "", subjects: [] })
        onClose()
      } else {
        alert("Error creating exam type")
      }
    } catch (error) {
      console.error("Create exam type error:", error)
      alert("Error creating exam type")
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setExamForm({ examName: "", examInstructions: "", subjects: [] })
    setExpandedInstructions({})
  }

  const calculateCurrentTotal = (subject) => {
    return subject.questionTypes.reduce((total, qType) => {
      return total + (Number.parseInt(qType.count) || 0)
    }, 0)
  }

  if (!showModal) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-5/6 max-w-6xl h-5/6 max-h-[800px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <BookOpen className="h-6 w-6 text-purple-600" />
            <h2 className="text-2xl font-bold text-gray-900">Create New Exam Type</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Exam Name */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border">
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                <Target className="inline h-5 w-5 mr-2 text-purple-600" />
                Exam Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={examForm.examName}
                onChange={(e) => setExamForm((prev) => ({ ...prev, examName: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg"
                placeholder="e.g., JEE Main, NEET, Custom Exam"
              />
            </div>

            {/* Exam Instructions */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border">
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                <FileText className="inline h-5 w-5 mr-2 text-blue-600" />
                Exam Instructions
              </label>
              <RichTextEditor
                value={examForm.examInstructions}
                onChange={(value) => setExamForm((prev) => ({ ...prev, examInstructions: value }))}
                placeholder="Enter detailed exam instructions here. You can format text, add lists, and style your instructions..."
              />
              <p className="text-sm text-gray-600 mt-2">
                Add comprehensive instructions for students including exam duration, marking scheme, and important
                guidelines.
              </p>
            </div>

            {/* Subjects Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">Subjects & Question Distribution</h3>
                <button
                  onClick={addSubject}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Subject</span>
                </button>
              </div>

              {examForm.subjects.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 text-lg">No subjects added yet</p>
                  <p className="text-gray-400 text-sm">Click "Add Subject" to get started</p>
                </div>
              )}

              {examForm.subjects.map((subject, subjectIndex) => {
                const currentTotal = calculateCurrentTotal(subject)
                const expectedTotal = Number.parseInt(subject.totalQuestions) || 0
                const isValidTotal = currentTotal === expectedTotal && expectedTotal > 0

                return (
                  <div key={subjectIndex} className="bg-white border-2 border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-800">Subject {subjectIndex + 1}</h4>
                      <div className="flex items-center space-x-2">
                        {subject.name && subject.totalQuestions && subject.questionTypes.length > 0 && (
                          <button
                            onClick={() => copyConfiguration(subjectIndex)}
                            className="flex items-center space-x-1 bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                            title="Copy this subject's configuration to a new subject"
                          >
                            <Copy className="h-3 w-3" />
                            <span>Copy Config</span>
                          </button>
                        )}
                        <button
                          onClick={() => removeSubject(subjectIndex)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={subject.name}
                          onChange={(e) => updateSubject(subjectIndex, "name", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., Physics, Chemistry, Mathematics"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Total Questions <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={subject.totalQuestions}
                          onChange={(e) => updateSubject(subjectIndex, "totalQuestions", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., 25, 60, 120"
                          min="1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Time (Minutes) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={subject.timeInMinutes}
                          onChange={(e) => updateSubject(subjectIndex, "timeInMinutes", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="e.g., 60, 90, 180"
                          min="1"
                        />
                      </div>
                    </div>

                    {expectedTotal > 0 && (
                      <div
                        className={`p-3 rounded-lg text-sm ${
                          isValidTotal
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-orange-50 text-orange-700 border border-orange-200"
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Hash className="h-4 w-4" />
                          <span>
                            Question Types Total: <strong>{currentTotal}</strong> / Expected:{" "}
                            <strong>{expectedTotal}</strong>
                            {isValidTotal ? " ✓" : " (needs adjustment)"}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Question Types */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-gray-700">
                          <Type className="inline h-4 w-4 mr-1" />
                          Question Types Distribution
                        </label>
                        <button
                          onClick={() => addQuestionType(subjectIndex)}
                          className="flex items-center space-x-1 bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                          <span>Add Type</span>
                        </button>
                      </div>

                      {subject.questionTypes.map((qType, typeIndex) => {
                        const instructionKey = `${subjectIndex}-${typeIndex}`
                        const isExpanded = expandedInstructions[instructionKey] !== false && qType.type

                        return (
                          <div key={typeIndex} className="bg-gray-50 p-4 rounded-lg space-y-3">
                            <div className="flex items-center space-x-3">
                              <div className="flex-1">
                                <select
                                  value={qType.type}
                                  onChange={(e) => updateQuestionType(subjectIndex, typeIndex, "type", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                >
                                  <option value="">Select Question Type</option>
                                  {SUPPORTED_QUESTION_TYPES.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="w-24">
                                <input
                                  type="number"
                                  value={qType.count}
                                  onChange={(e) => updateQuestionType(subjectIndex, typeIndex, "count", e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                  placeholder="Count"
                                  min="1"
                                />
                              </div>
                              {qType.type && (
                                <button
                                  onClick={() => toggleInstructionExpansion(subjectIndex, typeIndex)}
                                  className="flex items-center space-x-1 bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs hover:bg-indigo-200 transition-colors"
                                  title="Toggle instructions"
                                >
                                  <Info className="h-3 w-3" />
                                  {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                </button>
                              )}
                              <button
                                onClick={() => removeQuestionType(subjectIndex, typeIndex)}
                                className="text-red-500 hover:text-red-700 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Marks for Correct
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={qType.marksForCorrect}
                                  onChange={(e) =>
                                    updateQuestionType(subjectIndex, typeIndex, "marksForCorrect", e.target.value)
                                  }
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  placeholder="1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Marks for Incorrect
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={qType.marksForIncorrect}
                                  onChange={(e) =>
                                    updateQuestionType(subjectIndex, typeIndex, "marksForIncorrect", e.target.value)
                                  }
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  placeholder="-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Marks for Not Answered
                                </label>
                                <input
                                  type="number"
                                  step="0.1"
                                  value={qType.marksForLeaving}
                                  onChange={(e) =>
                                    updateQuestionType(subjectIndex, typeIndex, "marksForLeaving", e.target.value)
                                  }
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  placeholder="0"
                                />
                              </div>
                            </div>

                            {qType.type && isExpanded && (
                              <div className="border-t border-gray-200 pt-3">
                                <label className="block text-xs font-medium text-gray-600 mb-2">
                                  Instructions for {qType.type}
                                </label>
                                <RichTextEditor
                                  value={qType.instructions}
                                  onChange={(value) =>
                                    updateQuestionType(subjectIndex, typeIndex, "instructions", value)
                                  }
                                  placeholder={`Enter specific instructions for ${qType.type} questions...`}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  These instructions will be shown to students for this question type. You can format
                                  text with bold, italics, colors, and more.
                                </p>
                              </div>
                            )}
                          </div>
                        )
                      })}

                      {subject.questionTypes.length === 0 && (
                        <div className="text-center py-4 bg-gray-50 rounded border-dashed border-2 border-gray-300">
                          <Hash className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p className="text-gray-500 text-sm">No question types added</p>
                          <p className="text-gray-400 text-xs">Add question types from the dropdown menu</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={resetForm}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Reset Form
          </button>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center space-x-2 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <span>{loading ? "Creating..." : "Create Exam Type"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddQuestionAddExamType
